/**
 * ТЗ 0103 — палитра раздела «Маркетинг» одним модулем. Три словаря, которые
 * не пересекаются по смыслу:
 *  - каналы — `MARKETING_CHANNEL_COLORS` (`marketingModel`): Meta синяя,
 *    Google Ads оранжевый, SEO Google зелёный…;
 *  - показатели — здесь: лиды, договоры, продажи и расход тёмными тонами,
 *    которые НЕ совпадают ни с одним каналом (оранжевые лиды читались
 *    «Google Ads», зелёные продажи — «SEO Google», синий расход — «Meta»).
 *    Отличие от ближайшего канала — CIEDE2000 ≥ 14, контраст к белому ≥ 7:1;
 *  - курсы — по slug семейства из постоянного словаря: «Python» одного цвета
 *    в «Что приносит продажи», в разбивке тренда и в когортах, сколько бы
 *    курсов ни было в выборке и в каком бы порядке они ни пришли.
 * Сравнение поверх стека — нейтральным сланцем пунктиром (`molecules/Charts`).
 */

/** Цвета показателей (не каналов). */
export const MARKETING_METRIC_COLORS = {
  /** Лиды — сланцево-индиговый (≠ AI-рефералы, ≠ прямые). */
  crmLeads: '#2e3a59',
  /** Договоры — тёмный фиолетово-пурпурный. */
  contractsSent: '#86198f',
  /** Продажи — тёмный изумрудный (≠ SEO Google, ≠ Bing). */
  sales: '#065f46',
  /** Расход — нейтральный тёплый серый (≠ Meta). */
  spendPln: '#57534e',
} as const;

/** Прочие показатели в спарклайнах плиток — цвет подписей графиков. */
export const MARKETING_METRIC_OTHER_COLOR = '#475569';

/** Серый «прочего» и «не определено». */
export const MARKETING_SPLIT_REST_COLOR = '#94a3b8';

/**
 * Слои разбивки, у которых нет своего словаря (предложения, вид пути), — по
 * месту в легенде сервера.
 */
export const MARKETING_SPLIT_PALETTE = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#8b5cf6',
  '#e0a100',
  '#0891b2',
  '#db2777',
  '#65a30d',
  '#64748b',
];

/**
 * Палитра курсов (вариант Tableau 10, притемнён для белого фона): соседние
 * цвета различимы в стопке столбиков. Красного и зелёного в ней нет
 * (повторное ревью 0103, B8): в разделе они значат «плохо / хорошо», и
 * курс Data Science красным читался провалом — вместо них фиолетовый и
 * бирюзовый.
 */
export const MARKETING_FAMILY_PALETTE = [
  '#4e79a7',
  '#d9711c',
  '#3d8f89',
  '#8f5c86',
  '#0891b2',
  '#7c4dcc',
  '#8a644e',
  '#b8962e',
  '#d1708a',
  '#5f6db0',
];

/**
 * Постоянный словарь курсов: известные семейства — свой цвет навсегда;
 * новое семейство — цвет по хешу slug (тоже стабильный, но может
 * совпасть с соседом — тогда его стоит дописать сюда).
 */
export const MARKETING_FAMILY_COLORS: Readonly<Record<string, string>> = {
  'python-developer': MARKETING_FAMILY_PALETTE[0],
  java: MARKETING_FAMILY_PALETTE[1],
  frontend: MARKETING_FAMILY_PALETTE[2],
  qa: MARKETING_FAMILY_PALETTE[3],
  devops: MARKETING_FAMILY_PALETTE[4],
  'data-science': MARKETING_FAMILY_PALETTE[5],
  'cyber-security': MARKETING_FAMILY_PALETTE[6],
  kubernetes: MARKETING_FAMILY_PALETTE[7],
  'ai-agents': MARKETING_FAMILY_PALETTE[8],
  'zatrudnienie-w-it': MARKETING_FAMILY_PALETTE[9],
};

/** Детерминированный хеш строки (djb2) — номер цвета нового семейства. */
const slugHash = (slug: string): number =>
  Array.from(slug).reduce(
    (hash, char) => (hash * 33 + char.charCodeAt(0)) % 2147483647,
    5381,
  );

/**
 * Цвет курса по slug семейства: словарь, иначе — хеш slug; «не определено»
 * и «прочее» — серым.
 */
export const marketingFamilyColor = (
  slug: string | null | undefined,
  restKeys: ReadonlyArray<string> = [],
): string => {
  if (!slug || restKeys.includes(slug)) return MARKETING_SPLIT_REST_COLOR;
  return (
    MARKETING_FAMILY_COLORS[slug] ??
    MARKETING_FAMILY_PALETTE[slugHash(slug) % MARKETING_FAMILY_PALETTE.length]
  );
};
